import React, { useState, useEffect } from 'react';
import { Modal, Select, Radio, Checkbox, Button, message, Divider } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { scApi } from '../../../services/scApi';

// ── 列印選項介面定義 ──────────────────────────────────────────

/** 表頭類型 */
export type HeaderType = 'SALES_CONFIRMATION' | 'PROFORMA_INVOICE' | 'CONTRACT_SHEET';

/** 報表格式（含/不含表頭） */
export type ReportFormat = 'WITH_HEADER' | 'WITHOUT_HEADER';

/** 列印選項（傳給 API 的完整參數） */
export interface PrintOptions {
  headerType: HeaderType;
  format: ReportFormat;
  printSCReport: boolean;       // S/C 主報表
  printColorSample: boolean;    // 色樣手感表
  printInspection: boolean;     // 檢驗規範
  useA4Format: boolean;         // 使用 A4 格式
  approverId?: string;          // 簽核人員代碼
}

/** Modal 元件 Props */
interface PrintReportModalProps {
  visible: boolean;
  scNo: string;
  onClose: () => void;
  loading?: boolean;
}

// ── 選項常數 ──────────────────────────────────────────────────

/** 表頭類型選項 */
const HEADER_TYPE_OPTIONS: { value: HeaderType; label: string }[] = [
  { value: 'SALES_CONFIRMATION', label: 'SALES CONFIRMATION' },
  { value: 'PROFORMA_INVOICE', label: 'PROFORMA INVOICE' },
  { value: 'CONTRACT_SHEET', label: 'CONTRACT SHEET' },
];

// ── 元件 ──────────────────────────────────────────────────────

const PrintReportModal: React.FC<PrintReportModalProps> = ({
  visible,
  scNo,
  onClose,
  loading: externalLoading,
}) => {
  // 列印選項狀態（含預設值）
  const [headerType, setHeaderType] = useState<HeaderType>('SALES_CONFIRMATION');
  const [format, setFormat] = useState<ReportFormat>('WITH_HEADER');
  const [printSCReport, setPrintSCReport] = useState(true);
  const [printColorSample, setPrintColorSample] = useState(false);
  const [printInspection, setPrintInspection] = useState(false);
  const [useA4Format, setUseA4Format] = useState(false);
  const [approverId, setApproverId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  // 每次開啟 Modal 時重設為預設值
  useEffect(() => {
    if (visible) {
      setHeaderType('SALES_CONFIRMATION');
      setFormat('WITH_HEADER');
      setPrintSCReport(true);
      setPrintColorSample(false);
      setPrintInspection(false);
      setUseA4Format(false);
      setApproverId(undefined);
    }
  }, [visible]);

  // 驗證：至少勾選一個報表
  const hasSelectedReport = printSCReport || printColorSample || printInspection;

  /** 執行列印 */
  const handlePrint = async () => {
    if (!hasSelectedReport) {
      message.warning('請至少勾選一個列印報表');
      return;
    }

    const options: PrintOptions = {
      headerType,
      format,
      printSCReport,
      printColorSample,
      printInspection,
      useA4Format,
      approverId,
    };

    // 組合勾選的報表類型
    const reports: string[] = [];
    if (printSCReport) reports.push('P1');
    if (printColorSample) reports.push('P3');
    if (printInspection) reports.push('P4');

    // 用 GET URL 直接開啟新分頁，瀏覽器會用內建 PDF 檢視器預覽
    const url = scApi.getPrintPreviewUrl(scNo, reports.join(','), {
      headerType,
      format,
      useA4Format,
      approverId,
      reports: reports.join(','),
    });
    window.open(url, '_blank');
    onClose();
  };

  const isLoading = loading || externalLoading;

  return (
    <Modal
      title="列印選項"
      open={visible}
      onCancel={onClose}
      width={480}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          取消
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          loading={isLoading}
          disabled={!hasSelectedReport}
          onClick={handlePrint}
        >
          確定列印
        </Button>,
      ]}
    >
      {/* S/C 編號顯示 */}
      <p style={{ marginBottom: 16 }}>
        S/C：<strong>{scNo}</strong>
      </p>

      {/* ── 表頭類型 ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>表頭類型：</div>
        <Select
          value={headerType}
          onChange={setHeaderType}
          style={{ width: '100%' }}
          options={HEADER_TYPE_OPTIONS}
        />
      </div>

      {/* ── 報表格式 ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>報表格式：</div>
        <Radio.Group
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        >
          <Radio value="WITH_HEADER">含表頭</Radio>
          <Radio value="WITHOUT_HEADER">不含表頭 (A4)</Radio>
        </Radio.Group>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── 列印報表勾選 ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>列印報表：</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 8 }}>
          <Checkbox
            checked={printSCReport}
            onChange={(e) => setPrintSCReport(e.target.checked)}
          >
            S/C 主報表
          </Checkbox>
          <Checkbox
            checked={printColorSample}
            onChange={(e) => setPrintColorSample(e.target.checked)}
          >
            色樣手感表
          </Checkbox>
          <Checkbox
            checked={printInspection}
            onChange={(e) => setPrintInspection(e.target.checked)}
          >
            檢驗規範
          </Checkbox>
        </div>
        {/* 驗證提示 */}
        {!hasSelectedReport && (
          <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, paddingLeft: 8 }}>
            請至少勾選一個列印報表
          </div>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── A4 格式 ── */}
      <div style={{ marginBottom: 16 }}>
        <Checkbox
          checked={useA4Format}
          onChange={(e) => setUseA4Format(e.target.checked)}
        >
          使用 A4 格式
        </Checkbox>
      </div>

      {/* ── 簽核人員 ── */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>簽核人員：</div>
        <Select
          value={approverId}
          onChange={setApproverId}
          style={{ width: '100%' }}
          allowClear
          placeholder="選擇簽核人員（可選）"
          options={[]}  // TODO: 從後端載入簽核人員清單
        />
        <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
          留空則不顯示簽核欄位
        </div>
      </div>
    </Modal>
  );
};

export default PrintReportModal;
